import { useState } from "react";

import { MinusCircleFilled, PlusOutlined } from "@ant-design/icons";
import { useQuery } from "@apollo/client";
import { AutoComplete, Button, Form, Input, Select, Space } from "antd";

import { RVFormFloatingLabelWrap, QueryParams, RVFormItem } from "@regal-voice/ui";
import { getCampaigns } from "Services/marketing-api/campaigns/queries";
import { Campaign } from "Types/Campaign";
import { FieldLookup } from "Types/Segment";

import styles from "./UpdateTaskFormItems.module.scss";

const defaultQueryParams: Partial<QueryParams> = {
    pageSize: 100,
    orderBy: [
        {
            key: "updatedAt",
            value: "DESC",
        },
    ],
};

// ENUM CLEAN UP
// eslint-disable-next-line no-restricted-syntax
enum UpdateCriterion {
    ALL_TASKS = "All tasks",
    TASK_TYPE = "Task Type",
    CAMPAIGN = "Campaign",
}
const TASKS_TYPE = ["Inbound SMS", "Incoming Call", "Outbound Call", "ASAP Callback", "Scheduled Callback"];
const TASKS_TO_UPDATE: UpdateCriterion[] = [
    UpdateCriterion.ALL_TASKS,
    UpdateCriterion.TASK_TYPE,
    UpdateCriterion.CAMPAIGN,
];

const HARDCODED_TASK_EVENTS = [
    {
        label: "priority",
        value: "priority",
    },
    {
        label: "targetAgent",
        value: "targetAgent",
    },
    {
        label: "targetTeam",
        value: "targetTeam",
    },
    {
        label: "queue",
        value: "queue",
    },
    {
        label: "leadScore",
        value: "leadScore",
    },
];

export function UpdateTaskFormItems({ updateCriteria }: { updateCriteria?: UpdateCriterion }): JSX.Element {
    const [updateCriterion, setUpdateCriterion] = useState<UpdateCriterion | undefined>(updateCriteria);

    const { data } = useQuery(getCampaigns, {
        variables: {
            queryParams: defaultQueryParams,
        },
        fetchPolicy: "cache-and-network",
    });

    return (
        <>
            <RVFormFloatingLabelWrap
                name="updateCriteria"
                label="Select Update Criteria"
                showFormLabel={true}
                placeholder="Select Tasks to Update"
                rules={[
                    {
                        required: true,
                        message: "Update criteria is required",
                    },
                ]}
            >
                <Select
                    size="large"
                    onSelect={(value: UpdateCriterion) => setUpdateCriterion(value)}
                    showSearch
                    optionFilterProp="children"
                >
                    {TASKS_TO_UPDATE.map((criterion) => (
                        <Select.Option key={`updateCriteria-${criterion}`} value={criterion}>
                            {criterion}
                        </Select.Option>
                    ))}
                </Select>
            </RVFormFloatingLabelWrap>
            {updateCriterion == UpdateCriterion.TASK_TYPE && (
                <RVFormFloatingLabelWrap
                    name="taskType"
                    placeholder="Select Task Type"
                    rules={[
                        {
                            required: true,
                            message: "Task type is required",
                        },
                    ]}
                >
                    <Select size="large" showSearch optionFilterProp="children">
                        {TASKS_TYPE.map((type) => (
                            <Select.Option key={`updateCriteria-${type}`} value={type}>
                                {type}
                            </Select.Option>
                        ))}
                    </Select>
                </RVFormFloatingLabelWrap>
            )}
            {updateCriterion == UpdateCriterion.CAMPAIGN && (
                <RVFormFloatingLabelWrap
                    name="campaign"
                    placeholder="Select Campaign"
                    rules={[
                        {
                            required: true,
                            message: "Campaign is required",
                        },
                    ]}
                >
                    <Select size="large" showSearch optionFilterProp="children">
                        {data?.campaigns?.map((campaign: Campaign) => (
                            <Select.Option
                                key={`updateCriteria-campaign-${campaign.id}`}
                                value={`${campaign.name} (#${campaign.friendlyId})`}
                            >
                                <span>
                                    {campaign.name} ({campaign.friendlyId})
                                </span>
                            </Select.Option>
                        ))}
                    </Select>
                </RVFormFloatingLabelWrap>
            )}

            <RVFormItem label="Task Attributes to Add or Update">
                <Form.List
                    name="taskAttributesToAddUpdate"
                    rules={[
                        {
                            validator: async (_, headersKeyValuePairs: { attribute: string; value: string }[]) => {
                                if (!headersKeyValuePairs?.length) {
                                    return;
                                }
                                const set = new Set();
                                for (const pair of headersKeyValuePairs) {
                                    if (set.has(pair?.attribute)) {
                                        return Promise.reject(
                                            new Error("There are multiple attributes with same name")
                                        );
                                    }
                                    set.add(pair?.attribute);
                                }
                            },
                        },
                    ]}
                >
                    {(fields, { add, remove }, { errors }) => {
                        // form error list doesn't accept a class
                        const errorStyle = errors.length ? { marginTop: "-16px", paddingBottom: "8px" } : {};
                        return (
                            <>
                                {fields.map((field) => (
                                    <Space key={field.key} align="center" className={styles.row}>
                                        <RVFormFloatingLabelWrap
                                            {...field}
                                            name={[field.name, "attribute"]}
                                            placeholder="Attribute"
                                            rules={[{ required: true, message: "Missing attribute name" }]}
                                        >
                                            <AutoComplete options={HARDCODED_TASK_EVENTS} />
                                        </RVFormFloatingLabelWrap>
                                        <RVFormFloatingLabelWrap
                                            {...field}
                                            name={[field.name, "value"]}
                                            placeholder="Value"
                                            rules={[{ required: true, message: "Missing attribute value" }]}
                                        >
                                            <Input />
                                        </RVFormFloatingLabelWrap>

                                        <MinusCircleFilled
                                            onClick={() => remove(field.name)}
                                            className={styles.removeItem}
                                        />
                                    </Space>
                                ))}
                                <div style={errorStyle}>
                                    <Form.ErrorList errors={errors} />
                                </div>

                                <RVFormItem className={styles.addItem}>
                                    <Button onClick={() => add()} block icon={<PlusOutlined />}>
                                        Add Attribute
                                    </Button>
                                </RVFormItem>
                            </>
                        );
                    }}
                </Form.List>
            </RVFormItem>
            <RVFormFloatingLabelWrap
                name="taskAttributesToDelete"
                label="Task Attributes to Remove"
                showFormLabel={true}
                placeholder="Attributes to Remove"
            >
                <Select size="large" mode="tags">
                    {HARDCODED_TASK_EVENTS?.map((c: FieldLookup) => (
                        <Select.Option value={c.value} key={c.value}>
                            {c.label}
                        </Select.Option>
                    ))}
                </Select>
            </RVFormFloatingLabelWrap>
        </>
    );
}
